import { useCallback, useEffect, useState } from "react";
import type {
  FloatingToastState,
  FloatingToastTone,
} from "@/components/feedback/FloatingToast";

const FEEDBACK_TOAST_VISIBLE_MS = 3200;
const FEEDBACK_TOAST_EXIT_MS = 180;

type FloatingFeedbackToast = {
  title: string;
  message: string;
  tone?: FloatingToastTone;
  state: FloatingToastState;
};

export function useFloatingFeedbackToast() {
  const [toast, setToast] = useState<FloatingFeedbackToast | null>(null);

  useEffect(() => {
    if (!toast) return;

    const timeout = window.setTimeout(
      () => {
        if (toast.state === "exiting") {
          setToast(null);
          return;
        }

        setToast((current) =>
          current ? { ...current, state: "exiting" } : current,
        );
      },
      toast.state === "exiting"
        ? FEEDBACK_TOAST_EXIT_MS
        : FEEDBACK_TOAST_VISIBLE_MS,
    );

    return () => window.clearTimeout(timeout);
  }, [toast]);

  const showToast = useCallback(
    (nextToast: Omit<FloatingFeedbackToast, "state">) => {
      setToast({ ...nextToast, state: "visible" });
    },
    [],
  );

  const dismissToast = useCallback(() => {
    setToast((current) =>
      current ? { ...current, state: "exiting" } : current,
    );
  }, []);

  return { toast, showToast, dismissToast };
}
